import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { ComparatorModule } from './components/comparator/comparator.module';
import { SharedModule } from './shared/shared.module';
import { UsersModule } from './components/account/users.module';
import { CompanyModule } from './components/company/company.module';
import { PublicModule } from './public/public.module';

import { KeycloakAngularModule, KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { JwtInterceptor } from './shared/interceptors/jwt.interceptor';
import { HttpClientModule } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import { HttpErrorHandler } from './shared/service/error.service';
import { OperatorModule } from './components/operator/operator.module';
import { AnreModule } from './components/anre/anre.module';
import { SupplierModule } from './components/supplier/supplier.module';
import { ChatbotRasaModule } from './components/chatbot/chatbot-rasa.module';
import { SupplierService } from './components/supplier/services/supplier.service';
import { SupplierDetails } from './components/supplier/models/SupplierDetails';
import { OperatorService } from './components/operator/services/operator.service';
import { NavService } from './shared/service/nav.service';
import {SignatureModule} from './components/signature/signature.module';
import {MatDialogRef} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";

export function kcInitializer(keycloak: KeycloakService): () => Promise<any> {
  return (): Promise<any> => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        await keycloak.init(environment.keycloakOptions);
        console.log('Keycloak is initialized');
        // @ts-ignore
        resolve();
      } catch (error) {
        console.log('Error thrown in init ' + error);
        reject(error);
      }
    });
  };
}

export function initializeUserLogin(keycloakService: KeycloakService, navService: NavService,
  supplierService: SupplierService, operatorService: OperatorService): () => Promise<any> {
  return (): Promise<any> => {
    return new Promise((resolve, reject) => {
      try {
        keycloakService.keycloakEvents$.asObservable().subscribe(keycloakEvent => {
          if (
            KeycloakEventType.OnAuthSuccess === keycloakEvent.type ||
            KeycloakEventType.OnAuthRefreshSuccess === keycloakEvent.type
          ) {
            const userRoles = keycloakService.getUserRoles();
            keycloakService.loadUserProfile()
              .then(data => {
                localStorage.setItem('userProfile', JSON.stringify(data));
                resolve(data);
              })
              .catch(error => console.log(error));
            if (userRoles.indexOf('operator') > -1) {
              operatorService.getOperatorStatus().subscribe(
                (operator) => {
                  if (operator) {
                    if (operator.status !== 'VALIDATED') {
                      localStorage.setItem('validatedAndAdminRoleAssigned', 'false');
                    } else {
                      localStorage.setItem('validatedAndAdminRoleAssigned', 'true');
                      if (operator.licenceElectricity) {
                        localStorage.setItem('licentaElectricitate', 'true');
                      } else {
                        localStorage.setItem('licentaElectricitate', 'false');
                      }
                      if (operator.licenceGas) {
                        localStorage.setItem('licentaGaz', 'true');
                      } else {
                        localStorage.setItem('licentaGaz', 'false');
                      }
                    }
                  } else {
                    localStorage.setItem('validatedAndAdminRoleAssigned', 'false');
                    localStorage.setItem('licentaElectricitate', 'false');
                    localStorage.setItem('licentaGaz', 'false');
                  }
                  navService.updateMenuItems();
                }
              );
            }
            if (userRoles.indexOf('supplier') > -1) {
              supplierService.getSupplierDetails().subscribe(
                (supplier: SupplierDetails) => {
                  if (supplier.validatedAndAdminRoleAssigned === false) {
                    localStorage.setItem('validatedAndAdminRoleAssigned', 'false');
                  } else {
                    localStorage.setItem('validatedAndAdminRoleAssigned', 'true');
                    if (supplier.licenseElectricity) {
                      localStorage.setItem('licentaElectricitate', 'true');
                    } else {
                      localStorage.setItem('licentaElectricitate', 'false');
                    }
                    if (supplier.licenseGas) {
                      localStorage.setItem('licentaGaz', 'true');
                    } else {
                      localStorage.setItem('licentaGaz', 'false');
                    }
                  }
                  navService.updateMenuItems();
                }
              );
            }
            navService.updateMenuItems();
          }
        });
        // @ts-ignore
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    HttpClientModule,
    MatIconModule,
    BrowserAnimationsModule,
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    AppRoutingModule,
    ComparatorModule,
    SharedModule,
    UsersModule,
    CompanyModule,
    OperatorModule,
    SupplierModule,
    AnreModule,
    SignatureModule,
    KeycloakAngularModule,
    PublicModule,
    ChatbotRasaModule,
    HttpClientModule,
    ToastrModule.forRoot({
      positionClass: 'toast-top-right',
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: kcInitializer,
      multi: true,
      deps: [KeycloakService]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeUserLogin,
      deps: [KeycloakService, NavService, SupplierService, OperatorService],
      multi: true
    },
    CookieService,
    HttpErrorHandler
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
